import { Injectable } from '@angular/core';
import { GigyaService, Response } from './gigya.service';
import { OrganizationPickerService } from './organization-picker.service';

interface State {
  regToken?:string;
  expired:boolean;
  providers:Providers;
  pickedProvider?:{
    provider:Provider<any>;
    data?:any;
  };
}

interface Email {
  id: string;
  lastVerification: string;
  obfuscated: string;
}

interface Provider<T> {
  name: string;
  assertion?: string;
  data?: T
}

interface Providers {
  email: Provider<Email[]>;
}

@Injectable({
  providedIn: 'root'
})
export class MfaService {

  public state:State = {
    regToken: undefined,
    expired: false,
    providers: {
      email: {
        name: "gigyaEmail",
        assertion: undefined,
        data: undefined
      }
    }
  };

  constructor(
    private gigyaService:GigyaService,
    private organizationPicker:OrganizationPickerService
  ) {
    this.restore();
  }

  public async init(regToken:string): Promise<State> {
    this.state.regToken = regToken;

    const res = await this.gigyaService.execute(
      this.gigyaService.client.accounts.tfa.getProviders,
      { 
        regToken: regToken,
      }
    )
    console.log('res', res);

    if (res.errorCode !== 0) {
      this.state.expired = true;
      return this.state;
    }

    console.log('Active Providers >', res['activeProviders']);

    await this.initEmail();

    console.log('Providers ready', this.state.providers);
    this.save();
    return this.state;
  }

  public async sendEmailCode(): Promise<Response> {
    const res = await this.gigyaService.execute(
      this.gigyaService.client.accounts.tfa.email.sendVerificationCode,
      {
        gigyaAssertion: this.state.pickedProvider?.provider.assertion,
        regToken: this.state.regToken,
        emailID: this.state.pickedProvider?.data.emailId,
      }
    )
    this.state.pickedProvider!.data.phvToken = res['phvToken'];
    return res
  }

  public async submitEmailCode(code:string, remember:boolean): Promise<Response> {
    const res = await this.gigyaService.execute(
      this.gigyaService.client.accounts.tfa.email.completeVerification,
      {
        gigyaAssertion: this.state.pickedProvider?.provider.assertion,
        regToken: this.state.regToken,
        phvToken: this.state.pickedProvider?.data.phvToken,
        code: code,
        rememberDevice: remember,
      }
    )
    if (res.errorCode !== 0) {
      return res;
    }
    const r = await this.finalizeTfa(res['providerAssertion'], !remember);
    if (r.errorCode !== 0) {
      return r;
    }

    return this.finalizeRegistration(res['providerAssertion']);
  }

  public async generateBackupCodes(): Promise<Response> {
    const assertion = await this.initTfa("gigyaEmail", "register");
    return this.gigyaService.execute(
      this.gigyaService.client.accounts.tfa.backupcodes.create,
      {
        gigyaAssertion: assertion,
      }
    )
  }

  private async initEmail(): Promise<void> {
    this.state.providers.email.assertion = await this.initTfa("gigyaEmail");
    const emailRes = await this.gigyaService.execute(
      this.gigyaService.client.accounts.tfa.email.getEmails,
      {
        gigyaAssertion: this.state.providers.email.assertion,
      }
    )
    this.state.providers.email.data = emailRes['emails'];
    this.save();
  }

  private async initTfa(provider: string, mode = "verify"): Promise<string> {
    const res = await this.gigyaService.execute(
      this.gigyaService.client.accounts.tfa.initTFA,
      {
        regToken: this.state.regToken,
        provider: provider,
        mode: mode,
      }
    )
    return res['gigyaAssertion'];
  }

  private async finalizeTfa(assertion: string, tmpDevice = false): Promise<Response> {
    return this.gigyaService.execute(
      this.gigyaService.client.accounts.tfa.finalizeTFA,
      {
        tempDevice: tmpDevice,
        regToken: this.state.regToken,
        providerAssertion: assertion,
        gigyaAssertion: this.state.pickedProvider?.provider.assertion,
      }
    )
  }

  private async finalizeRegistration(assertion: string): Promise<Response> {
    const res = await this.gigyaService.execute(
      this.gigyaService.client.accounts.finalizeRegistration,
      {
        regToken: this.state.regToken,
      }
    )
    this.organizationPicker.proceed();
    return res;
  }

  private save() {
    try {
      const data = JSON.stringify(this.state);
      window.localStorage.setItem('mfaState', data);
    } catch {
      // localStorage unavailable (e.g. disabled/private browsing) - continue in-memory only
    }
  }

  private restore() {
    try {
      const data = window.localStorage.getItem('mfaState');
      if (data) {
        this.state = JSON.parse(data);
      }
    } catch {
      // localStorage unavailable or state corrupted - fall back to default state
    }
  }
}
